import { skipToken, useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';
import * as z from 'zod';

import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import {
    Alert,
    Button,
    LoadingIndicator,
    PageSplitDescription,
    PageSplitTitle,
    Separator,
    TextInput,
    type TextInputProps,
} from '@gitbook/spine-react';
import { localStorage } from '@gitbook/util-local-storage';
import { useHeadTitle } from '@gitbook/util-react-head';
import { useLoadingState } from '@gitbook/util-react-hooks';

import { MFAScreenModal, useHideSplashscreen } from '../../Components';
import { DocumentationLink } from '../../Components/Link';
import { useNavigationState, useSetAnalyticsScreen } from '../../navigation';
import { apiQuery, type FirebaseAuthProviderId, useAuthOperations } from '../../state';
import { LoginScreenLayout } from './LoginScreenLayout';
import { SSOOrganizationLogin } from './SSOOrganizationLogin';
import { SSOProviderLoginList } from './SSOProviderLoginList';

export const STORAGE_METHOD_KEY = '@login:method';
const STORAGE_EMAIL_KEY = '@login:email';

type SigninMethod = 'github' | 'google';

const emailSchema = z
    .string()
    .email({ message: 'Please enter a valid email address.' })
    .transform((val) => val.toLowerCase());

/*
 * Screen prompting the user to login.
 */
export function LoginScreen(): React.ReactElement {
    useHideSplashscreen(0);
    useHeadTitle('Join GitBook');

    return <LoginFlowStepPrompt />;
}

/**
 * Step to prompt for credentials.
 */
function LoginFlowStepPrompt() {
    const [useSSO, setUseSSO] = React.useState(true);
    const { screen } = useNavigationState();

    const organizationId =
        'organization' in screen && screen.organization ? screen.organization : null;

    const [{ data: organization }, { data: ssoProviders }] = useSuspenseQueries({
        queries: [
            apiQuery('getOrganizationById', organizationId ? { organizationId } : skipToken, {
                nullOnError: true,
            }),
            apiQuery(
                'listSSOProviderLoginsInOrganization',
                organizationId ? { organizationId } : skipToken,
                { nullOnError: true }
            ),
        ],
    });

    // If organization is configured with SSO, we prompt the SSO flow
    if (organization && ssoProviders?.items.length && useSSO && !screen.ssoLogin?.noSSO) {
        return (
            <SSOOrganizationLogin
                organization={organization}
                onUseNormalLogin={() => {
                    setUseSSO(false);
                }}
                loggedIn={false}
            />
        );
    }

    return (
        <StepPromptEmail
            initialEmail={screen.ssoLogin?.email}
            withNoSSOWarning={screen.ssoLogin?.noSSO === 'account_exists'}
        />
    );
}

function SwitchLoginSignupCTA(props: { children: React.ReactNode }) {
    return (
        <div className="text-[--sp-color-button-inline-text-default] hover:text-[--sp-color-button-inline-text-hover] focus-visible:[--sp-color-button-inline-text-focus]">
            {props.children}
        </div>
    );
}

/**
 * First step to prompt the email
 */
function StepPromptEmail(props: {
    initialEmail?: string;
    withNoSSOWarning?: boolean;
}): React.ReactElement {
    const { initialEmail, withNoSSOWarning } = props;

    const {
        mfa,
        fetchSignInMethodsForEmail,
        signInWithGitHub,
        signInWithGoogle,
        sendSignInLinkToEmail,
    } = useAuthOperations();
    const { screen } = useNavigationState();

    const [loadingGoogle, wrapLoadingGoogle] = useLoadingState(false);
    const [loadingGitHub, wrapLoadingGitHub] = useLoadingState(false);
    const [loadingEmail, wrapLoadingEmail] = useLoadingState(false);
    const loading = loadingGoogle || loadingGitHub || loadingEmail;

    const [sent, setSent] = React.useState(false);
    const [forgot, setForgot] = React.useState(false);
    const [methods, setMethods] = React.useState<FirebaseAuthProviderId[]>([]);
    const [emailInput, setEmailInput] = React.useState(
        initialEmail || localStorage.get<string>(STORAGE_EMAIL_KEY) || ''
    );
    const [emailFeedback, setEmailFeedback] = React.useState<TextInputProps['feedback']>();
    const hasMethods = methods && methods.length > 0;

    const [useSSOLogin, setUseSSOLogin] = React.useState<boolean>(false);
    const [ssoOrgId, setSSOOrgId] = React.useState<string | null>(null);
    const { data: ssoOrg } = useSuspenseQuery(
        apiQuery('getOrganizationById', ssoOrgId ? { organizationId: ssoOrgId } : skipToken, {
            // adding to prevent restructuring for APIErrorBoundary
            nullOnError: true,
        })
    );

    const isSignUp = screen.type === 'join';
    useSetAnalyticsScreen(forgot ? null : 'login');

    const onSignInWithEmail = async (email: string) => {
        wrapLoadingEmail(async () => {
            const result = await fetchSignInMethodsForEmail(email);
            if (!result) {
                return;
            }

            const { methods, ssoOrganization } = result;
            const otherMethods = methods.filter((method) => method !== 'emailLink');
            if (ssoOrganization) {
                // We systematically want to offer SSO login options even in alternate methods
                setSSOOrgId(ssoOrganization);
            }
            if (otherMethods.length === 0) {
                if (ssoOrganization) {
                    setUseSSOLogin(true);
                } else {
                    await sendSignInLinkToEmail(email);
                    setSent(true);
                }
            } else {
                setMethods(otherMethods);
            }
            await localStorage.set(STORAGE_EMAIL_KEY, email);
        });
    };

    const onSignInWithOAuth = async (method: SigninMethod) => {
        if (!method) {
            return;
        }

        if (method === 'github') {
            await wrapLoadingGitHub(() => signInWithGitHub());
        } else {
            await wrapLoadingGoogle(() => signInWithGoogle());
        }

        await localStorage.set(STORAGE_METHOD_KEY, method);
    };

    const onSubmit = () => {
        // Trim to avoid incorrect ccopy/pasting that are hard to identify
        const normalized = emailInput.trim();

        const validation = emailSchema.safeParse(normalized);

        if (validation.success) {
            if (emailInput !== normalized) {
                setEmailInput(normalized);
            }
            onSignInWithEmail(normalized);
        } else {
            setEmailFeedback({
                type: 'danger',
                message: validation.error.flatten().formErrors[0],
            });
        }
    };

    if (mfa) {
        return <MFAScreenModal mfa={mfa} />;
    }

    if (useSSOLogin && ssoOrg) {
        return (
            <SSOOrganizationLogin
                organization={ssoOrg}
                onUseNormalLogin={() => {}}
                loggedIn={false}
                onBack={() => setUseSSOLogin(false)}
            />
        );
    }

    if (hasMethods) {
        return (
            <StepAlternateMethods
                organization={ssoOrg}
                email={emailInput}
                methods={methods}
                onClose={() => setMethods([])}
            />
        );
    }

    if (sent) {
        return <StepCheckEmail email={emailInput} kind="sign-in" />;
    }

    if (forgot) {
        return <StepForgotPassword email={emailInput} onClose={() => setForgot(false)} />;
    }

    return (
        <LoginScreenLayout>
            <PageSplitTitle logo={<Icon.LogoSquare size={32} />}>
                {isSignUp ? (
                    <div className="heading-page">
                        <p className="text-base/60">Smarter docs.</p>
                        <p>Better product experience.</p>
                    </div>
                ) : (
                    'Sign in to GitBook'
                )}
            </PageSplitTitle>
            {withNoSSOWarning ? (
                <div className="mb-4 flex">
                    <Alert
                        kind="info"
                        icon={Icon.Key}
                        title="Log in with your existing credentials"
                        details={
                            <>
                                This account has not been linked to the SSO of your organization.
                                Contact your administrator to link it.{' '}
                                <DocumentationLink path="product-tour/sso-and-saml/saml/sso-members-vs-non-sso">
                                    Learn more here
                                </DocumentationLink>
                            </>
                        }
                    />
                </div>
            ) : null}
            {/* SSO logins first */}
            <div className="flex flex-col gap-6">
                <SigninButtons>
                    <Button
                        key="github"
                        size="large"
                        testID="login.githubButton"
                        kind="secondary"
                        disabled={loading}
                        leadingIcon={loadingGitHub ? LoadingIndicator : <Icon.GitHub size={16} />}
                        onPress={() => onSignInWithOAuth('github')}
                        label="Continue with GitHub"
                    />
                    <Button
                        key="google"
                        size="large"
                        disabled={loading}
                        testID="login.googleButton"
                        kind="secondary"
                        leadingIcon={loadingGoogle ? LoadingIndicator : <Icon.Google size={16} />}
                        onPress={() => onSignInWithOAuth('google')}
                        label="Continue with Google"
                    />
                </SigninButtons>

                <div className="flex items-center gap-2">
                    <Separator />
                    <p className="w-10 text-center text-muted text-ui-small leading-none">OR</p>
                    <Separator />
                </div>

                <div className="flex flex-col gap-4">
                    {/* Email should be at the bottom as an "alternative" method */}

                    <div className="flex w-full flex-col gap-4">
                        <div className="flex flex-col gap-2">
                            <TextInput
                                autoFocus
                                autoComplete="email"
                                data-testid="login.emailInput"
                                placeholder="Enter work email"
                                value={emailInput}
                                onChange={(event) => setEmailInput(event.target.value)}
                                onSubmit={() => onSubmit()}
                                feedback={emailFeedback}
                                disabled={loading}
                            />
                        </div>
                        <div className="flex grow">
                            <Button
                                kind="primary"
                                disabled={loading || !emailInput}
                                onPress={onSubmit}
                                leadingIcon={loadingEmail ? LoadingIndicator : undefined}
                                title="We'll send a magic link to your inbox to confirm your email address and sign you in."
                                size="large"
                                label="Continue"
                                className="w-full grow"
                            />
                        </div>
                    </div>
                    <div className="flex flex-col gap-2">
                        <p className="text-center text-[0.6875rem] text-muted">
                            By continuing, you agree to the{' '}
                            <DocumentationLink kind="inherit" host="policies" path="terms">
                                Terms of Service
                            </DocumentationLink>{' '}
                            and{' '}
                            <DocumentationLink
                                kind="inherit"
                                host="policies"
                                path="privacy-and-security/statement"
                            >
                                Privacy policy
                            </DocumentationLink>
                            .
                        </p>
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-center gap-2">
                {isSignUp ? (
                    <>
                        <SwitchLoginSignupCTA>Already have an account?</SwitchLoginSignupCTA>
                        <Button
                            kind="link"
                            label="Sign in"
                            href={{
                                screen: {
                                    type: 'root',
                                },
                            }}
                        />
                    </>
                ) : (
                    <>
                        <SwitchLoginSignupCTA>Get started with GitBook.</SwitchLoginSignupCTA>
                        <Button
                            kind="link"
                            label="Sign up"
                            href={{
                                screen: {
                                    type: 'join',
                                },
                            }}
                        />
                    </>
                )}
            </div>
        </LoginScreenLayout>
    );
}

/**
 * Renders signin buttons, highlighting the last used one when possible.
 * Child buttons must have a `key` set corresponding to the method they represent.
 * If it's the first login, renders all buttons.
 * If a method has been used before, moves that button first,
 * followed by a separator, then renders the others below.
 */
function SigninButtons(props: { children: React.ReactNode }) {
    const lastUsedSigninMethod = (() => {
        const lastUsedSigninMethod: SigninMethod | null = localStorage.get(STORAGE_METHOD_KEY);

        if (lastUsedSigninMethod !== null && ['google', 'github'].includes(lastUsedSigninMethod)) {
            return lastUsedSigninMethod;
        }

        return null;
    })();

    // If there's no last signing method, return all buttons normally
    if (lastUsedSigninMethod === null) {
        return <div className="flex flex-col gap-4 lg:flex-row lg:*:flex-1">{props.children}</div>;
    }

    // Else render the last used button first, followed by a separator, then the others
    const findChildByKey = (key: string) => {
        let foundChild: React.ReactNode | null = null;
        const otherChildren: React.ReactNode[] = [];

        React.Children.forEach(props.children, (child: React.ReactElement) => {
            if (child.key === key) {
                foundChild = child;
            } else {
                otherChildren.push(child);
            }
        });

        return [foundChild, otherChildren];
    };

    const [lastUsedSigninButton, otherButtons] = findChildByKey(lastUsedSigninMethod);

    return (
        <div className="flex w-full flex-col gap-4 lg:flex-row lg:*:flex-1">
            <div className="flex flex-col gap-2">
                {lastUsedSigninButton}
                {/* <p className="text-center text-muted text-ui-small">
                    Last used method
                </p> */}
            </div>
            <div className="*:w-full">{otherButtons}</div>
        </div>
    );
}

/**
 * Step to show alternate sign in methods for an email.
 */
function StepAlternateMethods(props: {
    email: string;
    methods: FirebaseAuthProviderId[];
    organization?: api.Organization | null;
    onClose: () => void;
}): React.ReactElement {
    const { email, methods, organization, onClose } = props;

    const { mfa, signInWithGoogle, signInWithGitHub, sendSignInLinkToEmail } = useAuthOperations();
    const [loadingGoogle, wrapLoadingGoogle] = useLoadingState(false);
    const [loadingGitHub, wrapLoadingGitHub] = useLoadingState(false);
    const [loadingEmail, wrapLoadingEmail] = useLoadingState(false);
    const loading = loadingGoogle || loadingGitHub || loadingEmail;
    const [withPassword, setWithPassword] = React.useState(false);
    const [sent, setSent] = React.useState(false);
    const { screen } = useNavigationState();

    const hasGoogle = methods.includes('google.com');
    const hasGitHub = methods.includes('github.com');
    const hasPassword = methods.includes('password');

    const onSignInWithGoogle = async () => {
        wrapLoadingGoogle(() => signInWithGoogle());
    };

    const onSignInWithGitHub = async () => {
        wrapLoadingGitHub(() => signInWithGitHub());
    };

    const onSendLink = async () => {
        wrapLoadingEmail(async () => {
            await sendSignInLinkToEmail(email);
            await localStorage.set(STORAGE_EMAIL_KEY, email);
            setSent(true);
        });
    };

    if (mfa) {
        return <MFAScreenModal mfa={mfa} />;
    }

    if (withPassword) {
        return <StepPromptPassword email={email} />;
    }

    if (sent) {
        return <StepCheckEmail email={email} kind="sign-in" />;
    }

    return (
        <LoginScreenLayout onClose={onClose}>
            <PageSplitTitle logo={<Icon.LogoSquare size={32} />}>
                Sign {screen.type === 'join' ? 'up' : 'in'} with your work email
            </PageSplitTitle>
            <PageSplitDescription>
                <p>
                    We have found alternate sign in methods for <strong>{email}</strong> that you
                    might be interested to use.
                </p>
            </PageSplitDescription>
            <div className="flex flex-col gap-4">
                {/* SSO Login options if any */}
                {organization ? (
                    <>
                        <div className="text-center">
                            Sign in to the <strong>{organization.title}</strong> organization using
                            SSO.
                        </div>

                        <SSOProviderLoginList organization={organization} />
                        <div className="text-center text-muted text-ui-small">
                            or use a standard method
                        </div>
                    </>
                ) : null}

                {/* Password */}
                {hasPassword ? (
                    <Button
                        kind="secondary"
                        size="large"
                        disabled={loading}
                        leadingIcon={Icon.Lock}
                        onPress={() => setWithPassword(true)}
                        label="Password"
                    />
                ) : null}
                {/* Google */}
                {hasGoogle ? (
                    <Button
                        kind="secondary"
                        size="large"
                        disabled={loading}
                        leadingIcon={loadingGoogle ? LoadingIndicator : Icon.Google}
                        onPress={onSignInWithGoogle}
                        label="Google"
                    />
                ) : null}
                {/* GitHub */}
                {hasGitHub ? (
                    <Button
                        kind="secondary"
                        size="large"
                        disabled={loading}
                        leadingIcon={loadingGitHub ? LoadingIndicator : Icon.GitHub}
                        onPress={onSignInWithGitHub}
                        label="GitHub"
                    />
                ) : null}

                {/* Link */}
                <div className="text-center text-muted text-ui-small">or we can simply</div>
                <Button
                    onPress={onSendLink}
                    disabled={loading}
                    leadingIcon={loadingEmail ? LoadingIndicator : undefined}
                    label="Send a link"
                    size="large"
                    kind="secondary"
                />
            </div>
        </LoginScreenLayout>
    );
}

/*
 * Step for a user to log in using his password.
 */
function StepPromptPassword(props: { email: string }): React.ReactElement {
    const { email } = props;

    const { mfa, signInWithEmailAndPassword } = useAuthOperations();
    const [password, setPassword] = React.useState('');
    const [loading, wrapLoading] = useLoadingState();
    const [forgot, setForgot] = React.useState(false);
    const { screen } = useNavigationState();

    const onSignInWithPassword = () => {
        wrapLoading(() => signInWithEmailAndPassword(email, password));
    };

    if (mfa) {
        return <MFAScreenModal mfa={mfa} />;
    }

    if (forgot) {
        return <StepForgotPassword email={email} onClose={() => setForgot(false)} />;
    }

    return (
        <LoginScreenLayout>
            <PageSplitTitle logo={<Icon.LogoSquare size={32} />}>
                Enter your password
            </PageSplitTitle>
            <PageSplitDescription>
                <p>
                    Enter the password associated with <strong>{email}</strong>.
                </p>
            </PageSplitDescription>
            {/* Password */}
            <div className="flex flex-col gap-4">
                <TextInput
                    autoFocus
                    data-testid="login.passwordInput"
                    placeholder="Your password"
                    autoComplete="current-password"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    onSubmit={() => onSignInWithPassword()}
                />
                <Button
                    label={`Sign ${screen.type === 'join' ? 'up' : 'in'}`}
                    kind="primary"
                    size="large"
                    onPress={onSignInWithPassword}
                    disabled={loading}
                    leadingIcon={loading ? LoadingIndicator : undefined}
                />
                <Button kind="link" label="Forgot your password?" onPress={() => setForgot(true)} />
            </div>
        </LoginScreenLayout>
    );
}

/**
 * Step to send a link to reset a password.
 */
function StepForgotPassword(props: { email: string; onClose: () => void }): React.ReactElement {
    const { onClose } = props;
    const { resetPassword } = useAuthOperations();
    const [loading, setLoading] = React.useState(false);
    const [email, setEmail] = React.useState(props.email);
    const [sent, setSent] = React.useState(false);

    useSetAnalyticsScreen('login_reset_password');

    const onForgotPassword = async () => {
        try {
            setLoading(true);

            const sent = await resetPassword(email);
            await localStorage.set(STORAGE_EMAIL_KEY, email);
            if (sent) {
                setSent(true);
            } else {
                onClose();
            }
        } finally {
            setLoading(false);
        }
    };

    if (sent) {
        return <StepCheckEmail email={email} kind="password-forgot" />;
    }

    return (
        <LoginScreenLayout onClose={onClose}>
            <PageSplitTitle logo={<Icon.LogoSquare size={32} />}>
                Forgot your password?
            </PageSplitTitle>
            <PageSplitDescription>
                Enter your email and we will send you a link to reset your password.
            </PageSplitDescription>
            {/* Password */}
            <div className="flex flex-col gap-4">
                <TextInput
                    autoFocus
                    data-testid="login.emailInput"
                    placeholder="name@example.com"
                    value={email}
                    autoComplete="email"
                    onChange={(event) => setEmail(event.target.value)}
                    onSubmit={() => onForgotPassword()}
                />
                <Button
                    leadingIcon={loading ? LoadingIndicator : undefined}
                    disabled={loading}
                    kind="primary"
                    onPress={onForgotPassword}
                    label="Send email"
                    size="large"
                />
            </div>
        </LoginScreenLayout>
    );
}

/*
 * Step to ask to check the inbox.
 */
function StepCheckEmail(props: {
    email: string;
    kind: 'sign-in' | 'password-forgot';
}): React.ReactElement {
    const { email, kind } = props;

    return (
        <LoginScreenLayout>
            <PageSplitTitle logo={<Icon.LogoSquare size={32} />}>Check your inbox</PageSplitTitle>
            <PageSplitDescription>
                {kind === 'sign-in' ? (
                    <p>
                        We've just sent an email confirmation to <strong>{email}</strong>, click the
                        link to verify your email.
                    </p>
                ) : (
                    <p>
                        We just emailed a reset link to <strong>{email}</strong>. Click the link,
                        and you'll be prompted to choose a new password.
                    </p>
                )}
            </PageSplitDescription>
        </LoginScreenLayout>
    );
}
